// MapView — the Intel page's map "shell".
//
// Mounts:
//   * <WorldMap /> — registry-driven deck.gl layers + basemap.
//   * HUD top-right — zoom buttons, AOI button, pause-stream button.
//   * <TimeScrubber /> — 56px bottom strip, sparkline + draggable handle.
//   * <EntityPopup /> when a signal is clicked.
//   * AOI tool: terra-draw polygon mode wired to `aoi` filter state.
//
// Keyboard:
//   `[` / `]`           — step asOf -1h / +1h
//   Shift+`[` / Shift+`]` — step asOf -6h / +6h
//   Esc                 — exit AOI mode + clear popup
//
// AOI flow:
//   * Click `[◇] AOI` in the HUD or fire `intel.openAoiTool` window event
//     (GeoPolygonFacet's "Edit AOI" button does the latter).
//   * Cursor becomes crosshair, terra-draw polygon mode active.
//   * Click vertices, double-click / Enter to close → polygon written to
//     FilterState.aoi via `onAoiAdd`.
//   * Hold Shift after closing for multi-AOI; otherwise the tool exits.
//   * Cap 25 per tenant — overflow triggers a toast (Phase D wires real toasts).

import { EntityPopup, WarZonePopup } from '@/world/EntityPopup';
import { WorldMap, type WorldMapAoi } from '@/world/WorldMap';
import type { BasemapVariant } from '@/world/basemapStyle';
import type { filters as f, intel as i } from '@eidollon/contracts';
import {
  type ContractMapPoint,
  INTEL_LAYERS,
  type RegionMetric,
  SAMPLE_NEWS,
  type WarZoneInfo,
  selectNewsForSignals,
  useTimeScrubber,
} from '@eidollon/intel-feed-client';
import type { GeoPolygonEntry } from '@eidollon/ui-filters';
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import { type GeoJSONStoreFeatures, TerraDraw, TerraDrawPolygonMode } from 'terra-draw';
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter';
import { tinykeys } from 'tinykeys';
import { TimeScrubber } from './TimeScrubber';
import { dispatchOpenNewsCard, useNewsCardController } from './useNewsCardController';
import { useRegionsMap } from './useRegionsMap';

// Non-critical overlays code-split — both render conditionally (legend
// behind a toggle, NewsCard only when a hot-zone marker is picked).
const MapLegend = lazy(() => import('@/world/MapLegend'));
const NewsCard = lazy(() => import('./NewsCard').then((m) => ({ default: m.NewsCard })));

/** Hot-zone threshold — severity ≥ 70 = ELEVATED / HOT (see colors.ts). */
const HOT_ZONE_SEVERITY = 70;

const AOI_CAP = 25;

export interface MapViewProps {
  signals: readonly i.IntelSignal[];
  /** Contract aggregates for the optional `contracts-hex` overlay. */
  contracts?: readonly ContractMapPoint[];
  /** Currently drawn AOIs from FilterState.aoi — rendered as overlays. */
  aois: readonly GeoPolygonEntry[];
  onAoiAdd: (next: GeoPolygonEntry) => void;
  onAoiRemove: (id: string) => void;
  enabledLayerIds?: ReadonlySet<string>;
  /** Layer-toggle callback used by MapLegend's "Active Layers" rows.
   *  Wired in route to mutate FilterState.signal_type so the legend
   *  toggle and the filter rail share one source of truth. */
  onToggleLayer?: (id: string) => void;
  /** Pause-stream callback — wired to useIntelFeed in Phase D. For now it
   *  just toggles a local flag that suppresses live merges. */
  paused?: boolean;
  onPauseToggle?: () => void;
  /** Active filter state — forwarded to news lookups so the hot-zone card
   *  + related-stories chip respect the same facet selection as the map. */
  filterState?: f.FilterState;
  /** Initial camera target (from URL state). */
  initialViewport?: { lng: number; lat: number; zoom: number };
  /** Fires after the user pans/zooms — already debounced to 150ms here so
   *  the URL doesn't thrash on every move event. */
  onViewportChange?: (next: { lng: number; lat: number; zoom: number }) => void;
  /** Lifted as-of clamp. When provided, MapView uses this instead of its
   *  internal `useTimeScrubber` state so URL state can sync. */
  asOf?: Date | null;
  onAsOfChange?: (next: Date | null) => void;
  /** Selected signal id (the EntityPopup is open for this signal). When the
   *  user picks/dismisses a marker, `onSelectSignal` fires so the parent can
   *  reflect that in URL state. */
  selectedSignalId?: string | null;
  onSelectSignal?: (id: string | null) => void;
  /** Basemap variant — wired from the page-level useState so the MapLegend
   *  toggle and the WorldMap stay in lockstep across re-renders. */
  basemapVariant?: BasemapVariant;
  onBasemapVariantChange?: (next: BasemapVariant) => void;
  /** Fires when the user clicks a `contracts-region` choropleth polygon.
   *  Parent drills the page's filter state into that region. */
  onRegionClick?: (props: RegionMetric) => void;
}

function isTextInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (/^(INPUT|TEXTAREA|SELECT)$/i.test(target.tagName)) return true;
  return target.isContentEditable;
}

function nextAoiId(): string {
  return `aoi-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

export function MapView({
  signals,
  contracts,
  aois,
  onAoiAdd,
  onAoiRemove,
  enabledLayerIds,
  onToggleLayer,
  paused = false,
  onPauseToggle,
  filterState = {},
  initialViewport,
  onViewportChange,
  asOf: asOfProp,
  onAsOfChange,
  selectedSignalId,
  onSelectSignal,
  basemapVariant = 'terrain',
  onBasemapVariantChange,
  onRegionClick,
}: MapViewProps) {
  const newsController = useNewsCardController();
  const [drawingAoi, setDrawingAoi] = useState(false);
  const [forceHeatmap, setForceHeatmap] = useState(false);
  const mapRef = useRef<MapRef | null>(null);
  const drawRef = useRef<TerraDraw | null>(null);

  // Viewport (zoom + bbox) tracked from WorldMap's onViewStateChange — drives
  // the contracts-region metric fetch (level = levelForZoom(zoom), bbox =
  // current bounds). Seeded to a world-ish view so the first fetch is valid
  // before the first move. WorldMap remains the camera source of truth; this
  // is a debounced mirror for the data layer.
  const [mapZoom, setMapZoom] = useState(initialViewport?.zoom ?? 3.5);
  const [mapBbox, setMapBbox] = useState<[number, number, number, number]>([-180, -85, 180, 85]);

  // contracts-region choropleth: only fetch region metrics when the layer is
  // enabled in the legend (default-on, but user can toggle off). Mirrors the
  // useContractsMap default-OFF gate pattern. T11.
  const regionLayerEnabled = enabledLayerIds ? enabledLayerIds.has('contracts-region') : true;
  const { regionFeatures } = useRegionsMap(regionLayerEnabled, mapZoom, mapBbox, filterState);

  // Time scrubber: when the parent owns `asOf` (URL state mode), wire it
  // through the local `useTimeScrubber` API shape by mirroring the prop.
  // Otherwise the scrubber operates in its standalone fallback mode.
  const localScrub = useTimeScrubber();
  const asOf = asOfProp !== undefined ? asOfProp : localScrub.asOf;
  const setAsOf = useCallback(
    (next: Date | null) => {
      localScrub.setAsOf(next);
      if (onAsOfChange) onAsOfChange(next);
    },
    [localScrub, onAsOfChange],
  );
  const setLive = useCallback(() => {
    localScrub.setLive();
    if (onAsOfChange) onAsOfChange(null);
  }, [localScrub, onAsOfChange]);
  const scrub = {
    asOf,
    setAsOf,
    setLive,
    range24h: localScrub.range24h,
  };

  // War-zone popup state — local only (war zones are static, not URL-synced).
  const [pickedWarZone, setPickedWarZone] = useState<WarZoneInfo | null>(null);

  // Selected-signal state: parent-controlled when `selectedSignalId` is
  // passed (URL state mode), local otherwise.
  const [pickedLocal, setPickedLocal] = useState<i.IntelSignal | null>(null);
  const picked =
    selectedSignalId !== undefined
      ? (signals.find((s) => s.id === selectedSignalId) ?? null)
      : pickedLocal;
  const setPicked = useCallback(
    (next: i.IntelSignal | null) => {
      setPickedLocal(next);
      if (onSelectSignal) onSelectSignal(next ? next.id : null);
    },
    [onSelectSignal],
  );

  // Debounced viewport reporter — fires 150ms after the last pan/zoom event.
  const viewportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (viewportTimerRef.current != null) clearTimeout(viewportTimerRef.current);
    };
  }, []);

  const renderedAois: WorldMapAoi[] = useMemo(
    () => aois.map((a) => ({ id: a.id, polygon: a.polygon })),
    [aois],
  );

  // Stable onPick reference. WorldMap's layer-build useMemo lists `onPick`
  // as a dep — passing an inline lambda would rebuild every deck.gl Layer
  // instance on every parent render, churning the MapboxOverlay diff and
  // tanking FPS. Captured filterState via a ref so the closure stays stable
  // across filter mutations without invalidating layers.
  const filterStateRef = useRef(filterState);
  filterStateRef.current = filterState;
  const handlePick = useCallback(
    (sig: i.IntelSignal | null) => {
      setPicked(sig);
      setPickedWarZone(null);
      // W2-B: hot-zone news popup. Only auto-fire on severity ≥ 70 so
      // lower-severity markers stay quiet (the EntityPopup still opens).
      if (sig && sig.severity >= HOT_ZONE_SEVERITY) {
        const matches = selectNewsForSignals(SAMPLE_NEWS, [sig.id], filterStateRef.current, 6);
        const primary = matches[0];
        if (primary) {
          const px = pickedMarkerAnchor(mapRef, sig);
          dispatchOpenNewsCard(primary, px);
        }
      }
    },
    [setPicked],
  );

  const handleWarZoneClick = useCallback(
    (zone: WarZoneInfo) => {
      setPicked(null);
      setPickedWarZone(zone);
    },
    [setPicked],
  );

  // Stable viewport-change handler. WorldMap re-renders whenever any prop
  // identity changes; an inline lambda here would hand it a fresh
  // `onViewStateChange` every parent render (≈1Hz from the live clock, more
  // during interaction), forcing a WorldMap re-render each time. setMapZoom /
  // setMapBbox are stable state setters and the timer lives in a ref, so the
  // only real dep is the parent's `onViewportChange`. The existing 150ms URL
  // debounce is preserved.
  const handleViewStateChange = useCallback(
    (vs: {
      longitude: number;
      latitude: number;
      zoom: number;
      bbox: [number, number, number, number];
    }) => {
      // Drive the region-metric fetch off the settled viewport (zoom →
      // level, bbox → intersect). WorldMap already debounces this to
      // moveEnd, so no extra throttle needed for the metric query.
      setMapZoom(vs.zoom);
      setMapBbox(vs.bbox);
      if (!onViewportChange) return;
      if (viewportTimerRef.current != null) clearTimeout(viewportTimerRef.current);
      viewportTimerRef.current = setTimeout(() => {
        onViewportChange({
          lng: vs.longitude,
          lat: vs.latitude,
          zoom: vs.zoom,
        });
      }, 150);
    },
    [onViewportChange],
  );

  // Snap-back: when the page first mounts and signals arrive, expand the
  // viewport to fit roughly. Covered by the basemap default; left for Phase D.

  // Keyboard: scrubber stepping, ESC to dismiss AOI.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `scrub` is a stable hook return whose internal `asOf`/`range24h` we read by reference inside the keyboard handler; rebinding the handler per render would unsubscribe + resubscribe tinykeys on every keystroke. `setPicked` + `toggleAoi` are stable setters from local state / parent props.
  useEffect(() => {
    const guard = (cb: (e: KeyboardEvent) => void) => (e: KeyboardEvent) => {
      if (isTextInput(e.target)) return;
      e.preventDefault();
      cb(e);
    };
    return tinykeys(window, {
      '[': guard(() => stepScrub(-1)),
      ']': guard(() => stepScrub(1)),
      'Shift+[': guard(() => stepScrub(-6)),
      'Shift+]': guard(() => stepScrub(6)),
      Escape: guard(() => {
        setPicked(null);
        setPickedWarZone(null);
        exitAoi();
      }),
    });
    function stepScrub(hours: number) {
      const base = scrub.asOf ?? scrub.range24h.to;
      const next = new Date(base.getTime() + hours * 60 * 60 * 1000);
      // Lock to live if we crossed back to (or past) the right edge.
      if (next.getTime() >= scrub.range24h.to.getTime()) {
        scrub.setLive();
        return;
      }
      // Clamp to the left edge.
      const min = scrub.range24h.from.getTime();
      scrub.setAsOf(new Date(Math.max(min, next.getTime())));
    }
    function exitAoi() {
      if (drawingAoi) toggleAoi(false);
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: `scrub` is a stable hook return; rebinding would resubscribe tinykeys per keystroke
  }, [scrub, drawingAoi]);

  // Listen for the GeoPolygonFacet bridge event — that button just dispatches
  // `intel.openAoiTool`; the MapView is the only place the actual draw lives.
  useEffect(() => {
    function onOpen() {
      toggleAoi(true);
    }
    window.addEventListener('intel.openAoiTool', onOpen);
    return () => window.removeEventListener('intel.openAoiTool', onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FPS auto-collapse — sample requestAnimationFrame, but only flip state
  // when a sustained transition is observed. The previous version called
  // setForceHeatmap on every frame whenever slow===0, which thrashed React
  // at 60Hz and rebuilt every deck.gl layer every frame. We now sample
  // ~4Hz: collapse after 8 consecutive slow frames, recover after 30
  // consecutive fast frames. The effect deps no longer include
  // forceHeatmap, so the RAF loop is not torn down on every flip.
  useEffect(() => {
    let raf = 0;
    let prev = performance.now();
    let slow = 0;
    let fast = 0;
    let collapsed = false;
    // Cooldown between auto-collapse flips. Without it, the sampler
    // oscillates on borderline hardware: collapse → fewer layers → frames
    // speed up → uncollapse → frames slow down → collapse, every ~0.5s.
    // That flip-flop reads as the whole map flickering (icons popping in and
    // out). 4s minimum dwell means at most one transition per 4s — adapts to
    // sustained slowness without the strobe. Uncollapse also needs ~1.5s of
    // sustained fast frames (90), not 0.5s, so a brief idle lull doesn't
    // immediately un-collapse a genuinely slow scene.
    let lastFlip = -Number.POSITIVE_INFINITY;
    const COOLDOWN_MS = 4000;
    const tick = (t: number) => {
      const dt = t - prev;
      prev = t;
      if (dt > 22) {
        slow += 1;
        fast = 0;
      } else {
        fast += 1;
        slow = 0;
      }
      const cool = t - lastFlip >= COOLDOWN_MS;
      if (cool) {
        if (!collapsed && slow >= 8) {
          collapsed = true;
          lastFlip = t;
          setForceHeatmap(true);
        } else if (collapsed && fast >= 90) {
          collapsed = false;
          lastFlip = t;
          setForceHeatmap(false);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // ── AOI lifecycle ─────────────────────────────────────────────────────
  const initDraw = useCallback(
    (map: MapRef) => {
      if (drawRef.current) return drawRef.current;
      const adapter = new TerraDrawMapLibreGLAdapter({ map: map.getMap() });
      const draw = new TerraDraw({
        adapter,
        modes: [new TerraDrawPolygonMode({ pointerDistance: 10 })],
      });
      draw.on('finish', (id) => {
        const features = draw.getSnapshot();
        const f = features.find((g) => g.id === id) as GeoJSONStoreFeatures | undefined;
        if (!f || f.geometry.type !== 'Polygon') return;
        if (aois.length >= AOI_CAP) {
          // Phase D wires real toasts; for now silently drop the
          // just-drawn polygon. The cap is documented next to the
          // constant. (semgrep `eidollon.spa.no-console` enforces no
          // console.* in the SPA — leaks to DevTools.)
          draw.removeFeatures([id]);
          return;
        }
        onAoiAdd({
          id: nextAoiId(),
          polygon: {
            type: 'Polygon',
            coordinates: f.geometry.coordinates as number[][][],
          },
        });
        // Clean the draw store; the AOI is now persisted in FilterState.
        draw.removeFeatures([id]);
      });
      drawRef.current = draw;
      return draw;
    },
    [aois.length, onAoiAdd],
  );

  const onMapReady = useCallback((map: MapRef) => {
    mapRef.current = map;
  }, []);

  // Stable onRegionClick reference for WorldMap. The parent may hand us a
  // fresh inline lambda each render; WorldMap lists `onRegionClick` in its
  // layer-build useMemo deps, so an unstable identity would rebuild the whole
  // deck.gl layer set on every parent re-render (≈1Hz clock + interaction).
  // We forward through a ref-backed stable callback so the identity never
  // changes. The `undefined`-passthrough is preserved: when the parent passes
  // no handler we pass `undefined` (so WorldMap leaves the region layer
  // non-clickable) rather than a stable-but-empty function.
  const onRegionClickRef = useRef(onRegionClick);
  onRegionClickRef.current = onRegionClick;
  const stableRegionClick = useCallback((props: RegionMetric) => {
    onRegionClickRef.current?.(props);
  }, []);
  const handleRegionClick = onRegionClick ? stableRegionClick : undefined;

  const toggleAoi = useCallback(
    (on?: boolean) => {
      const next = on ?? !drawingAoi;
      setDrawingAoi(next);
      const map = mapRef.current;
      if (!map) return;
      const draw = initDraw(map);
      if (next) {
        if (!draw.enabled) draw.start();
        draw.setMode('polygon');
      } else {
        if (draw.enabled) draw.stop();
      }
      // Cursor hint.
      map.getMap().getCanvas().style.cursor = next ? 'crosshair' : '';
    },
    [drawingAoi, initDraw],
  );

  // ── Render ─────────────────────────────────────────────────────────────

  const counts = useMemo(() => {
    const total = signals.length;
    const cutoff = scrub.asOf?.getTime();
    const visibleAfterScrub =
      cutoff != null ? signals.filter((s) => Date.parse(s.occurred_at) <= cutoff).length : total;
    return { total, visible: visibleAfterScrub };
  }, [signals, scrub.asOf]);

  // Default to "all layers enabled" when the parent doesn't pass an explicit
  // set — keeps the legend consistent with WorldMap's same-default behavior.
  const legendEnabled = useMemo<ReadonlySet<string>>(
    () => enabledLayerIds ?? new Set(INTEL_LAYERS.map((l) => l.id)),
    [enabledLayerIds],
  );

  return (
    <div
      style={{
        flex: 1,
        position: 'relative',
        background: '#050a10',
        minHeight: 0,
      }}
    >
      <WorldMap
        signals={signals}
        enabledLayerIds={enabledLayerIds}
        contracts={contracts}
        regionFeatures={regionFeatures}
        aois={renderedAois}
        initialViewport={initialViewport}
        onViewStateChange={handleViewStateChange}
        onPick={handlePick}
        onRegionClick={handleRegionClick}
        onWarZoneClick={handleWarZoneClick}
        onMapReady={onMapReady}
        forceHeatmap={forceHeatmap}
        asOf={scrub.asOf}
        variant={basemapVariant}
      />

      {/* HUD top-right */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 4,
          background: 'var(--bg-pane)',
          border: '1px solid var(--bd)',
          padding: 4,
          zIndex: 30,
        }}
      >
        <HudButton onClick={() => zoomBy(mapRef, 1)} label="+" title="zoom in" />
        <HudButton onClick={() => zoomBy(mapRef, -1)} label="−" title="zoom out" />
        <HudButton
          onClick={() => toggleAoi()}
          label="◇ AOI"
          title="draw an area of interest"
          on={drawingAoi}
        />
        {onPauseToggle && (
          <HudButton
            onClick={onPauseToggle}
            label={paused ? '▶' : '⏸'}
            title={paused ? 'resume live stream' : 'pause live stream'}
            on={paused}
          />
        )}
      </div>

      {/* AOI count chip — small, top-left */}
      {aois.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            padding: '0 8px',
            height: 22,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'var(--bg-pane)',
            border: '1px solid var(--cyan-line)',
            color: 'var(--cyan)',
            fontFamily: 'var(--mono)',
            fontSize: 11,
            zIndex: 30,
          }}
          title={`${aois.length} of ${AOI_CAP} AOIs`}
        >
          AOI {aois.length}/{AOI_CAP}
          {aois.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onAoiRemove(a.id)}
              aria-label={`remove AOI ${a.id}`}
              style={{ color: 'var(--fg-3)', marginLeft: 4 }}
            >
              ×
            </button>
          ))}
        </div>
      )}

      {/* Selected entity callout */}
      {picked && (
        <EntityPopup
          signal={picked}
          onClose={() => setPicked(null)}
          onPin={() => setPicked(null)}
          onCompare={() => setPicked(null)}
        />
      )}
      {/* War-zone callout — shown when user clicks a conflict region polygon */}
      {pickedWarZone && !picked && (
        <WarZonePopup zone={pickedWarZone} onClose={() => setPickedWarZone(null)} />
      )}

      {/* Map legend — pedagogical HUD. Sits bottom-left so it never
          collides with the top-right HUD or the AOI count chip. The
          TimeScrubber takes the full bottom edge; the legend sits 76px
          up so analysts can still read it while scrubbing. */}
      {onToggleLayer && (
        <Suspense fallback={null}>
          <MapLegend
            enabledLayerIds={legendEnabled}
            onToggleLayer={onToggleLayer}
            basemapVariant={basemapVariant}
            onBasemapVariantChange={onBasemapVariantChange ?? (() => undefined)}
          />
        </Suspense>
      )}

      {/* Time scrubber strip */}
      <TimeScrubber
        signals={signals}
        from={scrub.range24h.from}
        to={scrub.range24h.to}
        asOf={scrub.asOf}
        onAsOfChange={scrub.setAsOf}
        onLive={scrub.setLive}
        visibleCount={counts.visible}
      />

      {/* W2-B / W2-C: shared news card overlay. Both the map (high-severity
          marker pick) and the side panel (row click) dispatch
          `intel.openNewsCard`; the controller renders this one card. */}
      {newsController.card && (
        <Suspense fallback={null}>
          <NewsCard
            item={newsController.card.item}
            initialPosition={newsController.card.position}
            related={newsRelated(
              newsController.card.item,
              signals.filter((s) => s.severity >= HOT_ZONE_SEVERITY),
            )}
            onClose={newsController.close}
            onSelectRelated={(next) => newsController.open(next, newsController.card?.position)}
          />
        </Suspense>
      )}
    </div>
  );
}

/**
 * Resolve "where does this marker land on the screen" for the news-card
 * anchor. Best-effort: if we can't project (no geom, no map yet), fall back
 * to the viewport center. The card clamps itself into the viewport so a
 * stale anchor never strands the card offscreen.
 */
function pickedMarkerAnchor(
  ref: { current: MapRef | null },
  sig: i.IntelSignal,
): { x: number; y: number } | undefined {
  const map = ref.current?.getMap();
  if (!map || !sig.geom) return undefined;
  try {
    const pt = map.project([sig.geom.lng, sig.geom.lat]);
    // Anchor card to the right + below the marker so it doesn't cover it.
    return { x: Math.round(pt.x + 20), y: Math.round(pt.y + 20) };
  } catch {
    return undefined;
  }
}

/**
 * Pull related news items for the +N RELATED chip — any other hot-zone
 * news sharing at least one linked_signal_id with the active item, up to 5.
 */
function newsRelated(
  active: ReturnType<typeof selectNewsForSignals>[number],
  hotSignals: readonly i.IntelSignal[],
) {
  const hotIds = new Set(hotSignals.map((s) => s.id));
  const related = SAMPLE_NEWS.filter((r) => r.id !== active.id).filter(
    (r) =>
      r.linked_signal_ids.some((id) => hotIds.has(id)) ||
      r.linked_signal_ids.some((id) => active.linked_signal_ids.includes(id)),
  );
  return related.slice(0, 5);
}

function zoomBy(ref: { current: MapRef | null }, delta: number) {
  const map = ref.current?.getMap();
  if (!map) return;
  map.zoomTo(map.getZoom() + delta, { duration: 200 });
}

/**
 * Cesium camera zoom. `factor < 1` zooms in (camera halves altitude),
 * `factor > 1` zooms out. Uses camera.zoomIn/zoomOut on a moveAmount
 * derived from the current altitude so the gesture feels proportional
 * at any scale (zooming at street level moves a few meters; zooming at
 * orbit moves thousands of km).
 */
// biome-ignore lint/suspicious/noExplicitAny: Cesium type seam.
// biome-ignore lint/correctness/noUnusedVariables: 3D camera zoom helper, wired in T16 (Round 3 FPS downgrade) when cesium tilt becomes interactive.
function cesiumZoom(ref: { current: any }, factor: number) {
  const v = ref.current;
  if (!v || v.isDestroyed?.()) return;
  const pc = v.camera.positionCartographic;
  if (!pc) return;
  const moveAmount = pc.height * Math.abs(1 - factor);
  if (factor < 1) v.camera.zoomIn(moveAmount);
  else v.camera.zoomOut(moveAmount);
  v.scene.requestRender();
}

function HudButton({
  onClick,
  label,
  title,
  on = false,
}: { onClick: () => void; label: string; title: string; on?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={on}
      className="hud-btn"
      style={{
        height: 24,
        minWidth: 28,
        padding: '0 8px',
        background: on ? 'var(--primary-soft)' : 'transparent',
        border: `1px solid ${on ? 'var(--primary-line)' : 'var(--bd)'}`,
        color: on ? 'var(--primary)' : 'var(--fg-2)',
        fontFamily: 'var(--mono)',
        fontSize: 11,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
